import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { userService } from "../../services/UserService";
import { CheckCircle, RefreshCw, CircleSlash } from "lucide-react";
import { useLoader } from "../../context/LoaderContext";

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const { showLoader, hideLoader } = useLoader();
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading"
  );
  const [message, setMessage] = useState<string>("");

  useEffect(() => {
    const verifyUser = async () => {
      if (!token) {
        setMessage("Invalid or missing verification token.");
        setStatus("error");
        return;
      }
      showLoader();

      try {
        const { message } = await userService.verifyEmail(token);
        setMessage(message);
        setStatus("success");
        hideLoader();
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error.message ||
          "Failed to verify email.";
        setMessage(errorMessage);
        setStatus("error");
        hideLoader();
      }
    };

    verifyUser();
  }, [token]);

  const handleRetry = () => {
    setStatus("loading");
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  const handleContinue = () => {
    navigate("/auth");
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-background-default dark:bg-background-dark px-6">
      {status === "loading" && (
        <div className="flex flex-col items-center text-text-primary dark:text-text-primaryDark">
          <h2 className="text-2xl font-semibold mt-6">
            Verifying Your Email...
          </h2>
          <p className="text-sm text-text-secondary dark:text-text-secondaryDark mt-2">
            Hang tight, this process only takes a moment.
          </p>
        </div>
      )}

      {status === "success" && (
        <div className="flex flex-col items-center text-text-primary dark:text-text-primaryDark">
          <CheckCircle className="w-16 h-16 text-green-dark mb-6" />
          <h2 className="text-2xl font-semibold">
            Email Successfully Verified!
          </h2>
          <p className="text-sm text-text-secondary dark:text-text-secondaryDark mt-4">
            {message}
          </p>
          <button
            onClick={handleContinue}
            className="mt-6 px-6 py-3 border-2 border-green-dark text-green-dark rounded-lg font-bold text-sm hover:bg-green-dark hover:text-white transition"
          >
            Continue to Login
          </button>
        </div>
      )}

      {status === "error" && (
        <div className="flex flex-col items-center text-text-primary dark:text-text-primaryDark">
          <CircleSlash className="w-16 h-16 text-red-500 mb-6  stroke-[1]" />
          <h2 className="text-2xl font-semibold">Verification Failed</h2>
          <p className="text-sm text-text-secondary dark:text-text-secondaryDark mt-4">
            {message}
          </p>
          <button
            onClick={handleRetry}
            className="mt-6 px-6 py-3 border-2 border-red-500 text-red-500 rounded-lg font-bold text-sm hover:bg-red-500 hover:text-white transition flex items-center justify-center"
          >
            <RefreshCw className="w-5 h-5 mr-2" /> Retry Verification
          </button>
        </div>
      )}
    </div>
  );
};

export default VerifyEmail;
